/* eslint-disable max-lines */

import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import constants, {
  BASKET_STATUS_CHECKOUT,
  BASKET_STATUS_COMPLETE,
  BASKET_STATUS_ERROR,
  BASKET_STATUS_IN_PROGRESS,
  PAYMENT_STATUS_CONFIRMED,
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_PAID,
} from '../../constants';
import errorMappings, {
  DAS_CARD_DECLINED,
  DAS_PAYMENT_FAILED,
  DAS_UPDATE_CUSTOMER_FAILED,
} from '../../constants/errors';
import { getBasketPaymentsStatus, getBasketStatus } from '../../api/basket';
import { paymentOperations, paymentTypes } from '../../store/payments';

import React from 'react';
import { bookingOperations } from '../../store/booking';
import { createError } from '../../store/errors';
import { createPayment } from '../../api/payments';
import { getBooking } from '../../api/booking';
import { getBookings } from '../../store/bookings/actions';
import { getTicketAllocations } from '../../api/tickets';
import { messageOperations } from '../../store/messages';

const { MAKE_PAYMENT_UPDATING } = paymentTypes;

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_TIMEOUT = 5000;

const {
  makePaymentSuccess,
  makePaymentFailed,
  makePaymentRequiresAction,
  makePaymentReset,
} = paymentOperations;
const { getBookingSuccess } = bookingOperations;
const { sendMessage } = messageOperations;

/* eslint-disable max-statements, max-lines-per-function */

const handlePaymentSuccess = function*(bookingReference, messagePayload) {
  const { booking, errors: bookingErrors } = yield call(
    getBooking,
    bookingReference,
  );
  if (bookingErrors) {
    yield all(bookingErrors.map(error => put(createError(error))));
  } else {
    const {
      error,
      errors: ticketAllocationsErrors,
      ticketAllocations,
    } = yield call(getTicketAllocations, booking.id);

    if (error) {
      yield put(createError(error));
    } else if (ticketAllocationsErrors) {
      yield all(ticketAllocationsErrors.map(err => put(createError(err))));
    } else {
      yield put(
        getBookingSuccess({
          ...booking,
          ticket_allocations: ticketAllocations,
        }),
      );
    }
  }
  yield put(getBookings());

  yield put(makePaymentSuccess());
  yield put(sendMessage(messagePayload));
  yield put(makePaymentReset());
};

const checkBasketStatus = function*(
  bookingReference,
  attempt = MAX_RETRY_ATTEMPTS,
) {
  // load basket status
  const { status } = yield call(getBasketStatus, bookingReference);

  // eslint-disable-next-line no-use-before-define
  yield call(handleBasketStatus, bookingReference, status, attempt);
};

const checkPaymentsStatus = function*(bookingReference) {
  // load basket payments status
  const { payments } = yield call(getBasketPaymentsStatus, bookingReference);

  // eslint-disable-next-line no-use-before-define
  yield call(handlePaymentsStatus, bookingReference, payments);
};

const handleSuccess = function*(bookingReference) {
  yield call(handlePaymentSuccess, bookingReference, {
    type: 'success',
    displayType: 'toast',
    message: constants.SUCCESSFUL_PAYMENT,
  });
};

const handleSuccessWithError = function*(bookingReference, payments) {
  // eslint-disable-next-line no-unused-vars
  const [payment, ...bin] = payments;
  const messageNode = (
    <>
      <h3 className="text-center">
        Your payment has been successful but there has been an error completing
        your booking.
      </h3>
      <p className="text-center">
        Your payment reference is: <strong>{payment.id}</strong>. Please contact
        our Customer Services team who will resolve this for you.
      </p>
      <p className="text-center">
        <strong>Please do not try to re-book.</strong>
      </p>
    </>
  );
  yield call(handlePaymentSuccess, bookingReference, {
    displayType: 'prompt',
    type: 'warning',
    header: null,
    message: messageNode,
    actions: [
      {
        type: 'success',
        text: 'OK',
      },
    ],
    actioned: false,
    required: true,
  });
};

const handleBasketStatus = function*(bookingReference, basketStatus, attempt) {
  if (basketStatus === BASKET_STATUS_COMPLETE) {
    // success completed
    yield call(handleSuccess, bookingReference);
    return;
  }

  if (basketStatus === BASKET_STATUS_CHECKOUT) {
    // awaiting payment state - so we re-check basket status
    if (attempt > 0) {
      const nextAttempt = attempt - 1;
      // getBasketStatus after a delay
      yield delay(RETRY_TIMEOUT);
      yield call(checkBasketStatus, bookingReference, nextAttempt);
      return;
    }
    // ran out of attempts
    // check payment status
    yield call(checkPaymentsStatus, bookingReference);
    return;
  }

  if (basketStatus === BASKET_STATUS_IN_PROGRESS) {
    yield call(checkPaymentsStatus, bookingReference);
    return;
  }

  if (basketStatus === BASKET_STATUS_ERROR) {
    yield call(checkPaymentsStatus, bookingReference);
    return;
  }

  throw new Error(`Unhandled basket status ${basketStatus}`);
};

const handlePaymentsStatus = function*(bookingReference, payments) {
  // check for missing payments
  if (payments.length === 0) {
    // missing payments - implied fail
    yield put(makePaymentFailed());
    yield put(createError({ code: DAS_PAYMENT_FAILED }));
    return;
  }

  // check for any successes
  const successfulPayments = payments.filter(
    ({ paid, status }) =>
      paid === true ||
      [PAYMENT_STATUS_PAID, PAYMENT_STATUS_CONFIRMED].includes(status),
  );
  if (successfulPayments.length > 0) {
    // success with error message (contact CS)
    yield call(handleSuccessWithError, bookingReference, successfulPayments);
    return;
  }

  // check last payment from payments
  const [lastPayment] = payments.slice(-1);
  if (!lastPayment || lastPayment.status === PAYMENT_STATUS_FAILED) {
    // failed
    yield put(makePaymentFailed());
    yield put(createError({ code: DAS_PAYMENT_FAILED }));
    return;
  }

  throw new Error(`Unhandled basket payments status ${lastPayment.status}`);
};

const legacyMakePayment = function*({ payload }) {
  try {
    const data = yield call(createPayment, payload);
    const { errors } = data;
    const { requires_action, payment_intent_client_secret } = data.payment;
    const {
      card_number,
      cardholder_name,
      expiry_date,
      toast_message,
    } = payload;

    if (errors) {
      // Handle web service errors.
      yield all(
        errors.map(error =>
          put(
            createError({
              ...error,
              code: errorMappings[error?.code] ? error.code : DAS_CARD_DECLINED,
            }),
          ),
        ),
      );
      yield put(makePaymentFailed());
    } else if (requires_action) {
      yield put(
        makePaymentRequiresAction(
          payment_intent_client_secret,
          card_number,
          cardholder_name,
          expiry_date,
        ),
      );
    } else {
      yield call(handlePaymentSuccess, payload.bookingReference, {
        type: 'success',
        displayType: 'toast',
        message: toast_message ?? constants.SUCCESSFUL_PAYMENT,
      });
    }
  } catch (error) {
    yield put(
      createError({
        code: DAS_UPDATE_CUSTOMER_FAILED,
      }),
    );
  }
};

const performMakePayment = function*({ payload }) {
  if (!payload.basket_id) {
    yield call(legacyMakePayment, { payload });
    return;
  }

  const {
    card_number,
    cardholder_name,
    expiry_date,
    bookingReference,
  } = payload;

  try {
    const { errors, payment = {} } = yield call(createPayment, payload);

    if (errors) {
      yield all(
        errors.map(error =>
          put(
            createError({
              ...error,
              code: errorMappings[error?.code] ? error.code : DAS_CARD_DECLINED,
            }),
          ),
        ),
      );
      yield put(makePaymentFailed());
      return;
    }

    const { requires_action, payment_intent_client_secret } = payment;

    if (requires_action) {
      yield put(
        makePaymentRequiresAction(
          payment_intent_client_secret,
          card_number,
          cardholder_name,
          expiry_date,
        ),
      );
      return;
    }

    yield call(checkBasketStatus, bookingReference);
  } catch (error) {
    yield put(makePaymentFailed());
    yield put(createError({ code: DAS_PAYMENT_FAILED }));
  }
};

export {
  checkBasketStatus,
  checkPaymentsStatus,
  handlePaymentSuccess,
  handleSuccess,
  handleSuccessWithError,
  handleBasketStatus,
  handlePaymentsStatus,
  legacyMakePayment,
  performMakePayment,
  MAX_RETRY_ATTEMPTS,
  RETRY_TIMEOUT,
};

export default takeLatest(MAKE_PAYMENT_UPDATING, performMakePayment);
